import { useMemo } from 'react';
import type { RobotState, RobotSummary } from '@fabricos/protocol-ts';

interface Props {
  robot: RobotSummary;
  state?: RobotState;
}

/**
 * CSS-3D "wireframe" drone viewer that rotates with live ATTITUDE.
 *
 * Why CSS-3D and not three.js: avoids a runtime dep (~600 kB gzipped) for a
 * widget whose entire job is "show roll/pitch/yaw on an abstract drone
 * silhouette". The model is built from CSS transforms — a body box, N arms,
 * N spinning propellers — composed via `perspective` + `transform-style:
 * preserve-3d`. Arm count adapts to the autopilot's frame class (quad / hex
 * / octo / Y6 / tri / heli).
 *
 * Rotations come straight from `state.attitude` in radians. We invert the
 * vehicle-NED conventions for the visualization:
 *   roll  → rotateY  (vehicle rolling right = right arm tips down)
 *   pitch → rotateX  (vehicle pitching forward = nose down)
 *   yaw   → rotateZ  (vehicle yawing right = clockwise looking down)
 */
export function DroneViewer3D({ robot, state }: Props) {
  const { armCount, label } = frameSpec(robot);
  const attitude = state?.attitude;
  const rollDeg = attitude ? (attitude.roll_rad * 180) / Math.PI : 0;
  const pitchDeg = attitude ? (attitude.pitch_rad * 180) / Math.PI : 0;
  const yawDeg = attitude ? (attitude.yaw_rad * 180) / Math.PI : 0;
  const isArmed = state?.arm === 'armed';

  const arms = useMemo(() => {
    const arr: number[] = [];
    for (let i = 0; i < armCount; i++) {
      // Symmetric distribution around the body; +X is forward.
      arr.push((360 / armCount) * i);
    }
    return arr;
  }, [armCount]);

  return (
    <div className="flex h-full w-full flex-col items-center justify-center bg-base">
      {/* Heading + frame badge */}
      <div className="absolute left-3 top-3 font-mono text-[10px] uppercase tracking-[0.18em] text-fg-muted">
        {label}
      </div>
      <div className="absolute right-3 top-3 font-mono text-[10px] text-fg-muted">
        R {rollDeg.toFixed(1)}° · P {pitchDeg.toFixed(1)}° · Y {yawDeg.toFixed(1)}°
      </div>

      {/* The 3D stage */}
      <div
        style={{
          perspective: '900px',
          perspectiveOrigin: '50% 50%',
        }}
        className="h-[360px] w-[360px]"
      >
        <div
          style={{
            transformStyle: 'preserve-3d',
            transform: `rotateX(${-pitchDeg}deg) rotateY(${rollDeg}deg) rotateZ(${-yawDeg}deg)`,
            transition: 'transform 100ms linear',
          }}
          className="relative h-full w-full"
        >
          {/* Body — small cuboid centred at origin */}
          <div
            className="absolute"
            style={{
              left: '50%',
              top: '50%',
              width: '60px',
              height: '60px',
              marginLeft: '-30px',
              marginTop: '-30px',
              background: 'var(--color-surface-2)',
              border: '1px solid var(--color-border-default)',
              transform: 'translateZ(0)',
            }}
          />
          {/* Forward-facing nose marker */}
          <div
            className="absolute"
            style={{
              left: '50%',
              top: '50%',
              width: '6px',
              height: '24px',
              marginLeft: '-3px',
              marginTop: '-42px',
              background: 'var(--color-accent)',
              transform: 'translateZ(0)',
            }}
          />
          {/* Arms + props, one per frame motor */}
          {arms.map((angleDeg, i) => (
            <Arm key={i} angleDeg={angleDeg} index={i} armed={isArmed} />
          ))}
          {/* Horizon ring (visual reference plane at body level) */}
          <div
            className="absolute pointer-events-none"
            style={{
              left: '50%',
              top: '50%',
              width: '320px',
              height: '320px',
              marginLeft: '-160px',
              marginTop: '-160px',
              borderRadius: '50%',
              border: '1px dashed var(--color-border-subtle)',
              opacity: 0.4,
              transform: 'rotateX(90deg)',
            }}
          />
        </div>
      </div>

      {/* Pitch + roll ladders (2D overlays, not transformed) */}
      <div className="mt-2 grid grid-cols-3 gap-3 font-mono text-[10px] text-fg-muted">
        <span>ROLL <span className="text-fg-primary">{rollDeg.toFixed(1)}°</span></span>
        <span>PITCH <span className="text-fg-primary">{pitchDeg.toFixed(1)}°</span></span>
        <span>YAW <span className="text-fg-primary">{(((yawDeg % 360) + 360) % 360).toFixed(1)}°</span></span>
      </div>
    </div>
  );
}

function Arm({ angleDeg, index, armed }: { angleDeg: number; index: number; armed: boolean }) {
  return (
    <div
      className="absolute"
      style={{
        left: '50%',
        top: '50%',
        width: '120px',
        height: '6px',
        marginLeft: '0',
        marginTop: '-3px',
        background: 'var(--color-border-default)',
        transformOrigin: '0 50%',
        transform: `rotateZ(${angleDeg}deg) translateZ(0)`,
      }}
    >
      {/* Propeller — a thin spinning disc at the arm tip */}
      <div
        className="absolute"
        style={{
          left: '100px',
          top: '-18px',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          border: `2px solid ${armed ? 'var(--color-accent)' : 'var(--color-fg-muted)'}`,
          opacity: armed ? 0.9 : 0.6,
          animation: armed ? `spinProp 0.4s linear infinite` : 'none',
        }}
      >
        {/* Motor index */}
        <span
          className="absolute font-mono text-[9px] text-fg-muted"
          style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
        >
          {index + 1}
        </span>
      </div>
    </div>
  );
}

interface FrameSpec {
  armCount: number;
  label: string;
}

/** Derive arm count + display label from `robot.kind.frame`. Falls back
 *  to quad when the platform isn't a recognised multirotor. */
function frameSpec(robot: RobotSummary): FrameSpec {
  const frame = robot.kind.frame as { domain?: string; platform?: string; motors?: number | null };
  if (frame.motors && frame.motors > 0) {
    return { armCount: frame.motors, label: `${frame.platform ?? 'craft'} · ${frame.motors} motors` };
  }
  const map: Record<string, number> = {
    tricopter: 3,
    quadcopter: 4,
    hexacopter: 6,
    octocopter: 8,
    helicopter: 1,
    tiltrotor: 4,
    vtol: 4,
  };
  const platform = frame.platform ?? 'quadcopter';
  return { armCount: map[platform] ?? 4, label: platform };
}
